/**
 * Fall catcher flask horizontal travel limits — sea buoys at the left/right
 * stop points at the left/right edges of the runway.
 */
import { SEA_LINE_RATIO } from "../core/sky-clock.js";
import { gameImageUrl } from "../assets/game-image-urls.js";
import { getCachedImage } from "../assets/image-cache.js";
import * as C from "../config/constants.js";
import { getFallMouthHalfWidthPx } from "./fall-flask-shape.js";

export var FALL_MOVEMENT_RANGE = {
  BUOY_DISPLAY_HEIGHT: 34,
  BUOY_RADIUS: 9,
  BOB_AMPLITUDE: 1.75,
  BOB_RATE: 0.0022,
  SEA_DEPTH_FRAC: 0.22,
  SEA_DEPTH_MIN_PX: 10,
  /** Default buoy opacity when the flask is outside the near-edge band. */
  BUOY_BASE_ALPHA: 0.5,
  /** Play-width band (per side) where buoy opacity ramps between base and full. */
  BUOY_PROXIMITY_FRAC: 0.2,
};

/**
 * Vertical bob offset for a buoy at horizontal position `x`.
 * @param {number} x
 * @param {number} animTimeMs
 * @param {number} [scale]
 * @returns {number}
 */
export function getFallBuoyBobOffset(x, animTimeMs, scale) {
  if (animTimeMs == null || !isFinite(animTimeMs)) return 0;
  var s = scale != null && isFinite(scale) && scale > 0 ? scale : 1;
  return Math.sin(animTimeMs * FALL_MOVEMENT_RANGE.BOB_RATE + (x * 0.01))
    * FALL_MOVEMENT_RANGE.BOB_AMPLITUDE * s;
}

function getBuoyImage() {
  return getCachedImage(gameImageUrl("buoy"));
}

/**
 * @param {number} [scale]
 * @returns {{ width: number, height: number }}
 */
export function getFallBuoyDrawSize(scale) {
  var s = scale != null && isFinite(scale) && scale > 0 ? scale : 1;
  var height = FALL_MOVEMENT_RANGE.BUOY_DISPLAY_HEIGHT * s;
  var img = getBuoyImage();
  if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
    var aspect = img.naturalWidth / img.naturalHeight;
    return { width: height * aspect, height: height };
  }
  var fallback = FALL_MOVEMENT_RANGE.BUOY_RADIUS * 2 * s;
  return { width: fallback, height: fallback };
}

/**
 * Horizontal travel limits for the Fall catcher flask.
 *
 * `player.x` is the flask center. Gameplay clamps the center to
 * `[mouthHalfW, runWidth − mouthHalfW]`. Markers sit at the **outer
 * left/right edges of the flask sprite** at those extremes.
 *
 * Mouth half-width follows {@link getFallMouthHalfWidthPx} (`FALL_FLASK_COLLIDERS.mouth.w`),
 * not legacy `scale.flaskMouthWidth`.
 *
 * @param {number} runWidth
 * @param {object | null} [scale] layout scale (`runwayFlaskDisplayWidth`, etc.)
 * @param {number} [flaskW] sprite width for buoy placement at travel extremes
 * @param {{
 *   runHeight?: number,
 *   runConfig?: object,
 *   flaskGeometry?: object,
 *   wallThicknessPx?: number,
 *   cx?: number,
 * }} [mouthOpts]
 * @returns {{ minX: number, maxX: number, minPlayerX: number, maxPlayerX: number, mouthHalfW: number }}
 */
export function getFallMovementBounds(runWidth, scale, flaskW, mouthOpts) {
  mouthOpts = mouthOpts || {};
  var w = Math.max(1, Number(runWidth) || 1);
  var mouthHalfW = getFallMouthHalfWidthPx({
    flaskGeometry: mouthOpts.flaskGeometry,
    scale: scale,
    runWidth: w,
    runHeight: mouthOpts.runHeight,
    runConfig: mouthOpts.runConfig,
    wallThicknessPx: mouthOpts.wallThicknessPx,
    cx: mouthOpts.cx != null && isFinite(mouthOpts.cx) ? mouthOpts.cx : w / 2,
  });
  if (mouthHalfW == null) {
    if (flaskW != null && isFinite(flaskW) && flaskW > 0) {
      mouthHalfW = flaskW * 0.13;
    } else {
      mouthHalfW = w * 0.05;
    }
  }
  mouthHalfW = Math.max(1, mouthHalfW);
  var halfFlaskW = flaskW != null && isFinite(flaskW) && flaskW > 0
    ? flaskW / 2
    : mouthHalfW * 2;

  var minPlayerX = mouthHalfW;
  var maxPlayerX = w - mouthHalfW;
  if (maxPlayerX < minPlayerX) {
    var cx = w / 2;
    minPlayerX = cx;
    maxPlayerX = cx;
  }

  return {
    minX: minPlayerX - halfFlaskW,
    maxX: maxPlayerX + halfFlaskW,
    minPlayerX: minPlayerX,
    maxPlayerX: maxPlayerX,
    mouthHalfW: mouthHalfW,
  };
}

/**
 * @param {number} runHeight
 * @param {number} [seaLineRatio]
 * @param {number} [scaleUi]
 * @returns {{ seaLineY: number, buoyY: number }}
 */
export function getFallBuoyAnchorY(runHeight, seaLineRatio, scaleUi) {
  var h = Math.max(1, Number(runHeight) || 1);
  var ratio = seaLineRatio != null && isFinite(seaLineRatio) ? seaLineRatio : SEA_LINE_RATIO;
  var scale = scaleUi != null && isFinite(scaleUi) && scaleUi > 0 ? scaleUi : 1;
  var seaLineY = h * ratio;
  var seaBandH = Math.max(0, h - seaLineY);
  var minDepth = FALL_MOVEMENT_RANGE.SEA_DEPTH_MIN_PX * scale;
  var buoyY = seaLineY + Math.max(minDepth, seaBandH * FALL_MOVEMENT_RANGE.SEA_DEPTH_FRAC);
  return { seaLineY: seaLineY, buoyY: buoyY };
}

/**
 * Buoy opacity from flask horizontal position: 50% by default, ramping to
 * fully opaque at the matching travel extreme across a band of
 * BUOY_PROXIMITY_FRAC × runWidth measured from that extreme.
 *
 * @param {"left" | "right"} side
 * @param {number} playerX flask center X in run space
 * @param {number} runWidth
 * @param {{ minPlayerX: number, maxPlayerX: number }} bounds
 * @returns {number}
 */
export function getFallBuoyProximityAlpha(side, playerX, runWidth, bounds) {
  var cfg = FALL_MOVEMENT_RANGE;
  var base = cfg.BUOY_BASE_ALPHA;
  var full = 1;
  var w = Math.max(1, Number(runWidth) || 1);
  var px = Number(playerX);
  if (!isFinite(px)) return base;
  bounds = bounds || { minPlayerX: 0, maxPlayerX: w };
  var band = w * cfg.BUOY_PROXIMITY_FRAC;

  if (side === "left") {
    var fullAt = bounds.minPlayerX;
    var fadeEnd = fullAt + band;
    if (px <= fullAt) return full;
    if (px >= fadeEnd) return base;
    return full + (base - full) * ((px - fullAt) / band);
  }

  if (side === "right") {
    var fullAtRight = bounds.maxPlayerX;
    var fadeStart = fullAtRight - band;
    if (px >= fullAtRight) return full;
    if (px <= fadeStart) return base;
    return base + (full - base) * ((px - fadeStart) / band);
  }

  return base;
}

/**
 * @param {{
 *   runWidth: number,
 *   runHeight: number,
 *   scale?: object | null,
 *   flaskW?: number,
 *   scaleUi?: number,
 *   simTime?: number,
 *   runConfig?: object,
 *   flaskGeometry?: object,
 *   playerX?: number,
 * }} opts
 * @returns {{ leftX: number, rightX: number, y: number, scaleUi: number, simTime: number }}
 */
export function captureFallBuoyFadeState(opts) {
  opts = opts || {};
  var runWidth = Math.max(1, Number(opts.runWidth) || 1);
  var runHeight = Math.max(1, Number(opts.runHeight) || 1);
  var scaleUi = opts.scaleUi != null && isFinite(opts.scaleUi) && opts.scaleUi > 0
    ? opts.scaleUi
    : 1;
  var flaskW = opts.flaskW != null && isFinite(opts.flaskW) && opts.flaskW > 0
    ? opts.flaskW
    : (C.RUN_CONFIG && C.RUN_CONFIG.RUNWAY_FLASK_DISPLAY_WIDTH) || 120;
  var wallW = opts.scale && opts.scale.flaskWallThickness != null
    ? opts.scale.flaskWallThickness
    : undefined;
  var mouthOpts = {
    runHeight: runHeight,
    runConfig: opts.runConfig,
    flaskGeometry: opts.flaskGeometry,
    wallThicknessPx: wallW,
    cx: opts.playerX != null && isFinite(opts.playerX) ? opts.playerX : runWidth / 2,
  };
  var bounds = getFallMovementBounds(runWidth, opts.scale, flaskW, mouthOpts);
  var anchors = getFallBuoyAnchorY(runHeight, opts.seaLineRatio, scaleUi);
  return {
    leftX: bounds.minX,
    rightX: bounds.maxX,
    y: anchors.buoyY,
    scaleUi: scaleUi,
    simTime: opts.simTime != null && isFinite(opts.simTime) ? opts.simTime : 0,
  };
}

function drawFallBuoyFallback(ctx, x, y, scale, alpha, highlight) {
  var cfg = FALL_MOVEMENT_RANGE;
  var radius = cfg.BUOY_RADIUS * scale;
  if (highlight) {
    radius *= 1.12;
    alpha = Math.min(1, alpha + 0.12);
  }

  ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
  ctx.lineWidth = Math.max(1, 1.5 * scale);
  ctx.beginPath();
  ctx.ellipse(x, y + radius * 1.05, radius * 1.15, radius * 0.34, 0, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = "rgba(168, 92, 58, 0.92)";
  ctx.fillRect(x - radius * 0.11, y + radius * 0.05, radius * 0.22, radius * 1.35);

  ctx.beginPath();
  ctx.arc(x, y, radius, Math.PI, 0);
  ctx.fillStyle = "rgba(228, 62, 48, 0.96)";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI);
  ctx.fillStyle = "rgba(248, 250, 252, 0.96)";
  ctx.fill();

  ctx.strokeStyle = "rgba(190, 45, 38, 0.85)";
  ctx.lineWidth = Math.max(1.2, radius * 0.14);
  ctx.beginPath();
  ctx.moveTo(x - radius * 0.92, y);
  ctx.lineTo(x + radius * 0.92, y);
  ctx.stroke();

  ctx.fillStyle = "rgba(255, 255, 255, 0.42)";
  ctx.beginPath();
  ctx.arc(x - radius * 0.28, y - radius * 0.28, radius * 0.22, 0, Math.PI * 2);
  ctx.fill();

  if (highlight) {
    ctx.strokeStyle = "rgba(255, 240, 180, 0.75)";
    ctx.lineWidth = Math.max(1.5, 2 * scale);
    ctx.beginPath();
    ctx.arc(x, y, radius * 1.18, 0, Math.PI * 2);
    ctx.stroke();
  }
}

export function collectFallBuoySpriteSpecs(opts) {
  opts = opts || {};
  var img = getBuoyImage();
  if (!img) return [];
  var runWidth = Math.max(1, Number(opts.runWidth) || 1);
  var runHeight = Math.max(1, Number(opts.runHeight) || 1);
  var flaskW = opts.flaskDims && opts.flaskDims.flaskW != null
    ? opts.flaskDims.flaskW
    : (C.RUN_CONFIG && C.RUN_CONFIG.RUNWAY_FLASK_DISPLAY_WIDTH) || 120;
  var scaleUi = opts.scaleUi != null && isFinite(opts.scaleUi) && opts.scaleUi > 0
    ? opts.scaleUi
    : 1;
  var introAlpha = opts.stageIntroAlpha != null && isFinite(opts.stageIntroAlpha)
    ? Math.max(0, Math.min(1, opts.stageIntroAlpha))
    : 1;
  var bounds = getFallMovementBounds(runWidth, opts.scale, flaskW);
  var anchors = getFallBuoyAnchorY(runHeight, undefined, scaleUi);
  var playerX = opts.playerX;
  if (playerX == null || !isFinite(playerX)) playerX = runWidth / 2;
  var size = getFallBuoyDrawSize(scaleUi);
  var leftAlpha = getFallBuoyProximityAlpha("left", playerX, runWidth, bounds) * introAlpha;
  var rightAlpha = getFallBuoyProximityAlpha("right", playerX, runWidth, bounds) * introAlpha;
  var originX = opts.originX || 0;
  var originY = opts.originY || 0;
  function spec(key, x, alpha) {
    var by = anchors.buoyY + getFallBuoyBobOffset(x, opts.currentTime, scaleUi);
    return {
      key: key,
      image: img,
      x: originX + x,
      y: originY + by,
      width: size.width,
      height: size.height,
      alpha: alpha,
      zIndex: 5,
    };
  }
  return [
    spec("buoy-left", bounds.minX, leftAlpha),
    spec("buoy-right", bounds.maxX, rightAlpha),
  ];
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {{ alpha?: number, scale?: number, highlight?: boolean, currentTime?: number }} [opts]
 */
export function drawFallBuoy(ctx, x, y, opts) {
  if (!ctx) return;
  opts = opts || {};
  var scale = opts.scale != null && isFinite(opts.scale) ? opts.scale : 1;
  var alpha = opts.alpha != null ? opts.alpha : FALL_MOVEMENT_RANGE.BUOY_BASE_ALPHA;
  var size = getFallBuoyDrawSize(scale);
  var drawW = size.width;
  var drawH = size.height;
  if (opts.highlight) {
    drawW *= 1.08;
    drawH *= 1.08;
    alpha = Math.min(1, alpha + 0.12);
  }

  var bob = getFallBuoyBobOffset(x, opts.currentTime, scale);
  y += bob;

  ctx.save();
  ctx.globalAlpha = alpha;

  var img = getBuoyImage();
  if (img) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, x - drawW / 2, y - drawH / 2, drawW, drawH);
    if (opts.highlight) {
      ctx.strokeStyle = "rgba(255, 240, 180, 0.75)";
      ctx.lineWidth = Math.max(1.5, 2 * scale);
      ctx.beginPath();
      ctx.ellipse(x, y, drawW * 0.52, drawH * 0.52, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else {
    drawFallBuoyFallback(ctx, x, y, scale, alpha, opts.highlight);
  }

  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ leftX: number, rightX: number, y: number, scaleUi: number, simTime: number }} snap
 * @param {number} alpha
 */
export function drawFallBuoyFadePair(ctx, snap, alpha) {
  if (!ctx || !snap || alpha <= 0) return;
  drawFallBuoy(ctx, snap.leftX, snap.y, {
    alpha: alpha,
    scale: snap.scaleUi,
    currentTime: snap.simTime,
  });
  drawFallBuoy(ctx, snap.rightX, snap.y, {
    alpha: alpha,
    scale: snap.scaleUi,
    currentTime: snap.simTime,
  });
}

/**
 * GPU sprites for the Fall→Shake catcher fade, matching live Fall buoy layout.
 * @param {{ leftX: number, rightX: number, y: number, scaleUi: number, simTime: number }} snap
 * @param {number} alpha
 * @returns {Array<{ key: string, image: CanvasImageSource, x: number, y: number, width: number, height: number, alpha: number, zIndex: number }>}
 */
export function collectFallBuoyFadeSpriteSpecs(snap, alpha) {
  var img = getBuoyImage();
  if (!img || !snap || !(alpha > 0)) return [];
  var size = getFallBuoyDrawSize(snap.scaleUi);
  function spec(key, x) {
    var y = snap.y + getFallBuoyBobOffset(x, snap.simTime, snap.scaleUi);
    return {
      key: key,
      image: img,
      x: x,
      y: y,
      width: size.width,
      height: size.height,
      alpha: alpha,
      zIndex: 5,
    };
  }
  return [
    spec("fade-buoy-left", snap.leftX),
    spec("fade-buoy-right", snap.rightX),
  ];
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{
 *   runWidth: number,
 *   runHeight: number,
 *   playerX: number,
 *   scale?: object | null,
 *   scaleUi?: number,
 *   flaskDims?: { flaskW: number, flaskH: number, flaskTopY: number },
 *   currentTime?: number,
 *   stageIntroAlpha?: number,
 *   runConfig?: object,
 *   flaskGeometry?: object,
 * }} opts
 */
export function drawFallMovementRange(ctx, opts) {
  if (!ctx) return;
  opts = opts || {};
  var runWidth = Math.max(1, Number(opts.runWidth) || 1);
  var runHeight = Math.max(1, Number(opts.runHeight) || 1);
  var flaskW = opts.flaskDims && opts.flaskDims.flaskW != null
    ? opts.flaskDims.flaskW
    : (C.RUN_CONFIG && C.RUN_CONFIG.RUNWAY_FLASK_DISPLAY_WIDTH) || 120;
  var scaleUi = opts.scaleUi != null && isFinite(opts.scaleUi) && opts.scaleUi > 0
    ? opts.scaleUi
    : 1;
  var introAlpha = opts.stageIntroAlpha != null && isFinite(opts.stageIntroAlpha)
    ? Math.max(0, Math.min(1, opts.stageIntroAlpha))
    : 1;
  var playerX = opts.playerX;
  if (playerX == null || !isFinite(playerX)) playerX = runWidth / 2;
  var wallW = opts.scale && opts.scale.flaskWallThickness != null
    ? opts.scale.flaskWallThickness
    : undefined;
  var mouthOpts = {
    runHeight: runHeight,
    runConfig: opts.runConfig,
    flaskGeometry: opts.flaskGeometry,
    wallThicknessPx: wallW,
    cx: playerX,
  };
  var bounds = getFallMovementBounds(runWidth, opts.scale, flaskW, mouthOpts);
  var anchors = getFallBuoyAnchorY(runHeight, undefined, scaleUi);

  var playerX = opts.playerX;
  if (playerX == null || !isFinite(playerX)) playerX = runWidth / 2;
  var leftAlpha = getFallBuoyProximityAlpha("left", playerX, runWidth, bounds) * introAlpha;
  var rightAlpha = getFallBuoyProximityAlpha("right", playerX, runWidth, bounds) * introAlpha;

  drawFallBuoy(ctx, bounds.minX, anchors.buoyY, {
    alpha: leftAlpha,
    scale: scaleUi,
    currentTime: opts.currentTime,
  });
  drawFallBuoy(ctx, bounds.maxX, anchors.buoyY, {
    alpha: rightAlpha,
    scale: scaleUi,
    currentTime: opts.currentTime,
  });
}